import { startTransition, useEffect, useEffectEvent, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { AnalysisOutputCodeParameter } from "../ars_ldm";
import { PATCH_ARS_STATE, useArsContext } from "../contexts";
import { isAnalysisOutputProgrammingCodeValue } from "../typeGuards";
import { AnalysisOutputCodeParameterControl } from "./AnalysisOutputCodeParameterControl";

/**
 * Props for the AnalysisOutputProgrammingCodeParametersControl component.
 */
export interface AnalysisOutputProgrammingCodeParametersControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisOutputProgrammingCode in ArsContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing AnalysisOutputProgrammingCode parameters.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisOutputProgrammingCode in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the analysis output code parameters editor.
 */
export const AnalysisOutputProgrammingCodeParametersControl = ({
  dataPath,
  disabled = false,
}: AnalysisOutputProgrammingCodeParametersControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const programmingCode = getFromDataPath(dataPath, state, isAnalysisOutputProgrammingCodeValue);
  const parameters = programmingCode?.parameters ?? [];
  const [activeTab, setActiveTab] = useState<string>(parameters.length > 0 ? "parameter-0" : "");

  /**
   * Handles adding an analysis output code parameter.
   */
  const handleAddParameter = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && programmingCode) {
      const parameterCount = parameters.length;
      const newParameter: AnalysisOutputCodeParameter = {
        name: `Parameter_${parameterCount + 1}`,
        label: `Parameter ${parameterCount + 1}`,
        description: "",
        value: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/parameters/${parameterCount}`,
        value: newParameter,
      });
    }
  };

  /**
   * Event handler to update the active tab.
   *
   * @param newTab - Tab key to activate.
   */
  const updateActiveTab = useEffectEvent((newTab: string) =>
    startTransition(async () => setActiveTab(newTab)),
  );

  /** Move tabs */
  useEffect(() => {
    if (parseInt(activeTab.split("-")[1], 10) >= parameters.length) {
      updateActiveTab(parameters.length > 0 ? "parameter-0" : "");
    } else if (activeTab === "" && parameters.length > 0) {
      updateActiveTab("parameter-0");
    }
  }, [parameters.length, activeTab]);

  const idRoute = dataPath.replace(/\//g, "-") + "-parameters";

  return (
    <Tab.Container
      activeKey={activeTab}
      onSelect={(k) => {
        /* istanbul ignore else */
        if (k) {
          setActiveTab(k);
        }
      }}
    >
      <Nav
        variant="tabs"
        className="mb-3"
      >
        {parameters.map((parameter, index) => (
          <Nav.Item key={index}>
            <Nav.Link eventKey={`parameter-${index}`}>{parameter.name}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Tab.Content>
        {parameters.map((parameter, index) => (
          <Tab.Pane
            eventKey={`parameter-${index}`}
            key={index}
          >
            <AnalysisOutputCodeParameterControl
              key={`${parameter.name}-${index}`}
              dataPath={`${dataPath}/parameters/${index}`}
              disabled={disabled}
              siblingCount={parameters.length}
            />
          </Tab.Pane>
        ))}
      </Tab.Content>
      <AsyncButton
        id={`${idRoute}-add-parameter`}
        onClick={handleAddParameter}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled || programmingCode === undefined}
      >
        <span className="me-2">Add parameter</span>
        <PlusCircle />
      </AsyncButton>
    </Tab.Container>
  );
};

AnalysisOutputProgrammingCodeParametersControl.displayName =
  "AnalysisOutputProgrammingCodeParametersControl";
